import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { ref, remove, runTransaction, set, update } from '@angular/fire/database';
import { ObjHlp } from 'src/lib/fliko/helpers/object.helper';
import { Collection, FgsOptions } from 'src/lib/fliko/types/database.type';
import { DbQuery, KeyOf } from 'src/lib/fliko/types/nestedKey.type';
import { Nullable, Nullish } from 'src/lib/fliko/types/typeish.type';
import { AsyncHlp } from '../../helpers/async.helper';
import { DbObjContext, DbObjectBase } from './dbObject';

// TODO move to shared
export type NulCol<T> = Nullable<Collection<T>>;

/**
 * Base of database collection
 */
export class DbCollectionBase<T> extends DbObjectBase<Collection<T>> {
  constructor(
    ctx$: Observable<DbObjContext | null>,
    query?: DbQuery,
    private options?: FgsOptions
  ) {
    super(ctx$, query);
  }

  private toEntries = (x: NulCol<T>) => (x ? Object.entries(x) : []);

  private toMap = (x: NulCol<T>) => new Map(this.toEntries(x));

  private toList = (x: NulCol<T>) =>
    this.options?.keyField
      ? this.toEntries(x).map(([k, v]) => ({ ...v, [this.options!.keyField!]: k }))
      : x
        ? Object.values(x)
        : [];

  /**
   * Get collection as observable object
   * @return {Observable<NulCol<T>>}
   */
  public getObject(): Observable<NulCol<T>> {
    return this.observe$;
  }

  /**
   * Get collection as observable entries
   * @return {Observable<[string, T][]>}
   */
  public getEntries(): Observable<[string, T][]> {
    return this.observe$.pipe(map((x) => this.toEntries(x)));
  }

  /**
   * Get collection as observable key-value map
   * @return {Observable<Map<string, T>>}
   */
  public getMap(): Observable<Map<string, T>> {
    return this.observe$.pipe(map((x) => this.toMap(x)));
  }

  /**
   * Get collection as observable list
   * @return {Observable<T[]>}
   */
  public getList(): Observable<T[]> {
    return this.observe$.pipe(map((x) => this.toList(x)));
  }

  /**
   * Get observable "child" element of collection
   * @param {string} key
   * @return {Observable<T | Nullish>}
   */
  public getElement(key: string): Observable<T | Nullish> {
    return this.observe$.pipe(map((x) => ObjHlp.get(x, key)));
  }

  /**
   * Get first emitted value of collection as object (async)
   * @return {Promise<NulCol<T>>}
   */
  public onceObject(): Promise<NulCol<T>> {
    return AsyncHlp.onFirst(this.observe$, filter((x) => !!x)); // prettier-ignore
  }

  /** Reads one collection response, including an empty collection, after the database is ready. */
  public snapshotObject(cancel$?: Observable<unknown>): Promise<NulCol<T>> {
    return AsyncHlp.onFirst(this.snapshot$(cancel$));
  }

  /** Reads one child response and releases its listener even when the child does not exist. */
  public snapshotElement(key: string, cancel$?: Observable<unknown>): Promise<T | Nullish> {
    return AsyncHlp.onFirst(this.snapshot$(cancel$).pipe(map((value) => ObjHlp.get(value, key))));
  }

  /**
   * Get first emitted value of collection as entries (async)
   * @return {Promise<[string, T][]>}
   */
  public onceEntries(): Promise<[string, T][]> {
    return AsyncHlp.onFirst(this.observe$.pipe(map(this.toEntries)), filter((x) => !!x)); // prettier-ignore
  }

  /**
   * Get first emitted value of collection as map (async)
   * @return {Promise<Map<string, T>>}
   */
  public onceMap(): Promise<Map<string, T>> {
    return AsyncHlp.onFirst(this.observe$.pipe(map(this.toMap)), filter((x) => !!x)); // prettier-ignore
  }

  /**
   * Get first emitted value of collection as list (async)
   * @return {Promise<T[]>}
   */
  public onceList(): Promise<T[]> {
    return AsyncHlp.onFirst(this.observe$.pipe(map(this.toList)), filter((x) => !!x)); // prettier-ignore
  }

  /**
   * Get first emitted value of collection child (async)
   * @param {string} key
   * @return {Promise<[string, T][]>}
   */
  public onceElement(key: string): Promise<T | Nullish> {
    return AsyncHlp.onFirst(this.observe$.pipe(map((x) => ObjHlp.get(x, key))), filter((x) => !!x)); // prettier-ignore
  }
}

/**
 * Database collection that only returns values
 */
export class DbCollectionQuery<T> extends DbCollectionBase<T> {
  constructor(ctx$: Observable<DbObjContext | null>, query: DbQuery) {
    super(ctx$, query);
  }
}

/**
 * Database collection with elements (children) manipulation
 */
export class DbCollection<T> extends DbCollectionBase<T> {
  constructor(ctx$: Observable<DbObjContext | null>, options?: FgsOptions) {
    super(ctx$, undefined, options);
  }

  /**
   * Set "child" element in database
   * @param {string} key
   * @param {T}      value
   * @return {Promise<void>}
   */
  public async setElement(key: string, value: T): Promise<void> {
    const ctx = await this.peekContext;
    return set(ref(ctx.dbRef, `${ctx.path}/${key}`), ObjHlp.sanitizeForDB(value));
  }

  /**
   * Set child of "child" element in database (if rules are stricter)
   * @param {string} elKey
   * @param {K}      childKey
   * @param {T[K]}   value
   * @return {Promise<void>}
   */
  public async setElementChild<K extends KeyOf<T>>(elKey: string, childKey: K, value: T[K]): Promise<void> {
    const ctx = await this.peekContext;
    return set(ref(ctx.dbRef, `${ctx.path}/${elKey}/${childKey}`), ObjHlp.sanitizeForDB(value as any));
  }

  /**
   * Set collection by object
   * @param {Collection<T>} obj
   * @return {Promise<void>}
   */
  public async setObject(obj: Collection<T>): Promise<void> {
    const ctx = await this.peekContext;
    const sanitized: Collection<T> = {};
    for (const id in obj) {
      sanitized[id] = ObjHlp.sanitizeForDB(obj[id]);
    }
    return set(ref(ctx.dbRef, ctx.path), sanitized);
  }

  /**
   * Update "child" element in database
   * @param {string}     key
   * @param {Partial<T>} value
   * @return {Promise<void>}
   */
  public async updateElement(key: string, value: Partial<T>): Promise<void> {
    const ctx = await this.peekContext;
    return update(ref(ctx.dbRef, `${ctx.path}/${key}`), ObjHlp.sanitizeForDB(value));
  }

  /**
   * Atomically updates an element. Returning null deletes it; returning undefined aborts the transaction.
   * Disclaimer: null handling was added on 21.08.2026. If a transaction behaves differently than expected, check
   * whether the updater returns null.
   * @param {string} key
   * @param {(current: T | null) => T | null | undefined} updater
   * @return {Promise<boolean>} true when the transaction was committed
   */
  public async transactElement(key: string, updater: (current: T | null) => T | null | undefined): Promise<boolean> {
    const ctx = await this.peekContext;
    const result = await runTransaction(ref(ctx.dbRef, `${ctx.path}/${key}`), (current: T | null) => {
      const updated = updater(current);
      return updated === undefined ? undefined : ObjHlp.sanitizeForDB(updated);
    });
    return result.committed;
  }

  /**
   * Update child of "child" element in database (if rules are stricter)
   * @param {string} elKey
   * @param {K}      childKey
   * @param {T[K]}   value
   * @return {Promise<void>}
   */
  public async updateElementChild<K extends KeyOf<T>>(elKey: string, childKey: K, value: T[K]): Promise<void> {
    const ctx = await this.peekContext;
    return update(ref(ctx.dbRef, `${ctx.path}/${elKey}/${childKey}`), ObjHlp.sanitizeForDB(value as any));
  }

  /**
   * Update collection by object
   * @param {Collection<T>} obj
   * @return {Promise<void>}
   */
  public async updateObject(obj: Collection<T>): Promise<void> {
    const ctx = await this.peekContext;
    const sanitized: Collection<T> = {};
    for (const id in obj) {
      sanitized[id] = ObjHlp.sanitizeForDB(obj[id]);
    }
    return update(ref(ctx.dbRef, ctx.path), sanitized);
  }

  /**
   * Update collection by entries
   * @param {[string, T][]} entries
   * @return {Promise<void>}
   */
  public async updateEntries(entries: [string, T][]): Promise<void> {
    const ctx = await this.peekContext;
    const sanitized = entries.map((e) => (e = [e[0], ObjHlp.sanitizeForDB(e[1])]));
    return update(ref(ctx.dbRef, ctx.path), Object.fromEntries(sanitized));
  }

  /**
   * Update collection by map
   * @param {Map<string, T>} map
   * @return {Promise<void>}
   */
  public async updateMap(map: Map<string, T>): Promise<void> {
    return this.updateEntries(Object.entries(map));
  }

  /**
   * Update collection by list
   * @param {T[]}      list
   * @param {KeyOf<T>} idKey key of object used as id (if not exist, list entry is omitted)
   * @return {Promise<void>}
   */
  public async updateList(list: T[], idKey: KeyOf<T>): Promise<void> {
    const ctx = await this.peekContext;
    const updateObj: any = {};
    list.forEach((l) => {
      const id = ObjHlp.get(l, idKey);
      if (id) updateObj[id] = ObjHlp.sanitizeForDB(l);
    });
    return update(ref(ctx.dbRef, ctx.path), updateObj);
  }

  /**
   * Remove "child" element from database
   * @param {string} key
   * @return {Promise<void>}
   */
  public async removeElement(key: string): Promise<void> {
    const ctx = await this.peekContext;
    return remove(ref(ctx.dbRef, `${ctx.path}/${key}`));
  }

  /**
   * Remove child of "child" element in database (if rules are stricter)
   * @param {string} elKey
   * @param {K}      childKey
   * @return {Promise<void>}
   */
  public async removeElementChild<K extends KeyOf<T>>(elKey: string, childKey: K): Promise<void> {
    const ctx = await this.peekContext;
    return remove(ref(ctx.dbRef, `${ctx.path}/${elKey}/${childKey}`));
  }

  // TODO transactElementChild
}
